import { Injectable, computed, signal } from "@angular/core";
import { DevAvailabilityContext } from "./dev-availability";

/**
 * Per-panel-instance state that rows read to decide whether they are
 * relevant to the current page and whether they can work right now.
 *
 * Provided by the dev panel component, not `providedIn: "root"`, so it is
 * rebuilt each time the panel opens.
 */
@Injectable()
export class DevPanelContext {
  private readonly url = signal("");
  private readonly offlineMode = signal(false);
  private readonly advisorMode = signal(false);

  /** Lowercased path segments of the current url, query string/fragment/matrix params removed. */
  readonly routeSegments = computed(() =>
    this.url()
      .split(/[?#]/)[0]
      .split("/")
      .map((segment) => segment.split(";")[0].trim().toLowerCase())
      .filter((segment) => segment !== ""),
  );

  readonly availability = computed<DevAvailabilityContext>(() => ({
    offlineMode: this.offlineMode(),
    advisorMode: this.advisorMode(),
  }));

  setUrl(url: string): void {
    this.url.set(url);
  }

  setOfflineMode(offlineMode: boolean): void {
    this.offlineMode.set(offlineMode);
  }

  setAdvisorMode(advisorMode: boolean): void {
    this.advisorMode.set(advisorMode);
  }

  /**
   * True when any of the row's route tags applies to the current url.
   *
   * The tag `"/"` is a sentinel for the dashboard: the root url has no path
   * segments at all, so it cannot be expressed as a segment name.
   */
  matchesRoute(tags: readonly string[]): boolean {
    const segments = this.routeSegments();
    return tags.some((tag) => {
      const normalized = tag.trim().toLowerCase();
      return normalized === "/" ? segments.length === 0 : segments.includes(normalized);
    });
  }
}
